import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormState } from "react-final-form";

import {
  type ObservationsMapParameterMapLayerConfiguration,
  type ObservationsMapWidgetConfiguration,
  type ParameterTimeSeriesConfiguration,
  type ParametersGeoBoundingBox,
  type StaticMapOverlaysOverlayConfiguration,
  type Widget,
} from "@/lib/api";
import { backfillObservationsMapTimeSeriesConfigurations } from "@/lib/api/observations-map-widget-configuration.utils";
import {
  useObservedMapLayersQuery,
  useObservedParametersQuery,
} from "@/features/shared/widgets/observations-map/hooks/queries";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import { ObservationsMapBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";
import { canonicalizeWidgetUpdatePayload } from "@/features/widget-forms/dialogs/is-unchanged-widget-update";
import { toMoved } from "@/lib/array";
import { useTranslation } from "@/lib/i18n";
import { initialViewportPresets } from "@/features/widget-forms/shared/initial-viewport-presets";
import { useStaticMapOverlaysQuery } from "@/features/widget-forms/forms/weather-model-map/queries";

import { applyTimeSeriesFixForParameter } from "./marker-time-series-sync";
import {
  type ObservationsMapDisplayGroupFormEntry,
  displayGroupsNeedFormIds,
  ensureDisplayGroupFormIds,
} from "./display-group-form.utils";

export function useObservationsMapForm() {
  const { t } = useTranslation();
  const form = useForm();
  const formState = useFormState({
    subscription: { values: true },
  });
  const baselineRef = useRef<unknown>(null);
  const [dirtyBaselineReady, setDirtyBaselineReady] = useState(false);

  const configuration: Partial<ObservationsMapWidgetConfiguration> =
    (formState.values.configuration as Partial<ObservationsMapWidgetConfiguration>) ?? {};

  const boundsFromConfig = configuration.initial_viewport_bounds;
  const initialViewportBounds: ParametersGeoBoundingBox =
    boundsFromConfig ?? initialViewportPresets.nl;

  const parameterMapLayerConfigurations: ObservationsMapParameterMapLayerConfiguration[] =
    configuration.parameter_map_layer_configurations ?? [];
  const selectedMapLayerIds = useMemo(
    () => parameterMapLayerConfigurations.map((c) => c.parameter_map_layer_id),
    [parameterMapLayerConfigurations],
  );
  const staticOverlayConfigurations: StaticMapOverlaysOverlayConfiguration[] =
    configuration.static_map_overlay_configurations ?? [];
  const timeSeriesConfigurations: ParameterTimeSeriesConfiguration[] =
    configuration.parameter_time_series_configurations ?? [];
  const displayGroups: ObservationsMapDisplayGroupFormEntry[] = useMemo(
    () => ensureDisplayGroupFormIds(configuration.parameter_display_groups ?? []),
    [configuration.parameter_display_groups],
  );
  const showAllLocations = configuration.show_all_locations ?? true;
  const weatherStationsEnabledByDefault =
    configuration.weather_stations_enabled_by_default ?? true;

  useEffect(() => {
    if (!displayGroupsNeedFormIds(configuration.parameter_display_groups)) return;
    form.change("configuration.parameter_display_groups", displayGroups);
  }, [configuration.parameter_display_groups, displayGroups, form]);
  const selectedStaticOverlayIds = useMemo(
    () => staticOverlayConfigurations.map((c) => c.static_map_overlay_id),
    [staticOverlayConfigurations],
  );

  const setInitialViewportBounds = useCallback(
    (bounds: ParametersGeoBoundingBox) => {
      form.change("configuration.initial_viewport_bounds", {
        ...bounds,
        north_latitude: clampLatitude(roundCoord(bounds.north_latitude)),
        south_latitude: clampLatitude(roundCoord(bounds.south_latitude)),
        east_longitude: clampLongitude(roundCoord(bounds.east_longitude)),
        west_longitude: clampLongitude(roundCoord(bounds.west_longitude)),
      });
    },
    [form],
  );

  const updateInitialViewportBoundsField = useCallback(
    (field: keyof Omit<ParametersGeoBoundingBox, "type">, rawValue: unknown) => {
      let parsed = parseCoord(rawValue);
      if (parsed !== undefined) {
        switch (field) {
          case "north_latitude":
          case "south_latitude":
            parsed = clampLatitude(parsed);
            break;
          case "east_longitude":
          case "west_longitude":
            parsed = clampLongitude(parsed);
            break;
        }
        form.change(`configuration.initial_viewport_bounds.${field}`, parsed);
      }
    },
    [form],
  );

  const baseLayerStyle =
    configuration.base_layer_style || ObservationsMapBaseLayerStyle.Monochrome;

  const setBaseLayerStyle = useCallback(
    (value: string) => {
      form.change("configuration.base_layer_style", value);
    },
    [form],
  );

  const { data: mapLayers } = useObservedMapLayersQuery();

  const { data: staticOverlays } = useStaticMapOverlaysQuery();

  const { data: observedParameters, isPending: parametersLoading } =
    useObservedParametersQuery();

  const parameters = useMemo(
    () => observedParameters?.map(toTimeSeriesParameterOption),
    [observedParameters],
  );

  useEffect(() => {
    if (!observedParameters?.length || timeSeriesConfigurations.length === 0) return;
    const catalog = observedParameters.map((parameter) => ({
      id: parameter.id,
      units: parameter.units,
    }));
    const backfilled = backfillObservationsMapTimeSeriesConfigurations(
      timeSeriesConfigurations,
      catalog,
    );
    const changed =
      backfilled.length !== timeSeriesConfigurations.length ||
      backfilled.some(
        (config, index) =>
          config.unit?.code !== timeSeriesConfigurations[index]?.unit?.code,
      );
    if (changed) {
      form.change("configuration.parameter_time_series_configurations", backfilled);
    }
  }, [observedParameters, timeSeriesConfigurations, form]);

  const addTimeSeriesConfig = useCallback(
    (config: ParameterTimeSeriesConfiguration) => {
      form.change("configuration.parameter_time_series_configurations", [
        ...timeSeriesConfigurations,
        config,
      ]);
    },
    [form, timeSeriesConfigurations],
  );

  const removeTimeSeriesConfig = useCallback(
    (configIndex: number) => {
      const updated = timeSeriesConfigurations.filter((_, i) => i !== configIndex);
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const reorderTimeSeriesConfigs = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.parameter_time_series_configurations",
        toMoved(timeSeriesConfigurations, fromIndex, toIndex),
      );
    },
    [form, timeSeriesConfigurations],
  );

  const updateTimeSeriesConfig = useCallback(
    (configIndex: number, config: ParameterTimeSeriesConfiguration) => {
      const updated = [...timeSeriesConfigurations];
      updated[configIndex] = config;
      form.change("configuration.parameter_time_series_configurations", updated);
    },
    [form, timeSeriesConfigurations],
  );

  const addDisplayGroup = useCallback(
    (group: ObservationsMapDisplayGroupFormEntry) => {
      form.change("configuration.parameter_display_groups", [...displayGroups, group]);
    },
    [form, displayGroups],
  );

  const removeDisplayGroup = useCallback(
    (groupIndex: number) => {
      form.change(
        "configuration.parameter_display_groups",
        displayGroups.filter((_, i) => i !== groupIndex),
      );
    },
    [form, displayGroups],
  );

  const reorderDisplayGroups = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.parameter_display_groups",
        toMoved(displayGroups, fromIndex, toIndex),
      );
    },
    [form, displayGroups],
  );

  const updateDisplayGroup = useCallback(
    (groupIndex: number, group: ObservationsMapDisplayGroupFormEntry) => {
      const updated = [...displayGroups];
      updated[groupIndex] = group;
      form.change("configuration.parameter_display_groups", updated);
    },
    [form, displayGroups],
  );

  const setDisplayGroups = useCallback(
    (groups: ObservationsMapDisplayGroupFormEntry[]) => {
      form.change("configuration.parameter_display_groups", groups);
    },
    [form],
  );

  const setShowAllLocations = useCallback(
    (value: boolean) => {
      form.change("configuration.show_all_locations", value);
    },
    [form],
  );

  const setWeatherStationsEnabledByDefault = useCallback(
    (value: boolean) => {
      form.change("configuration.weather_stations_enabled_by_default", value);
    },
    [form],
  );

  const ensureParameterInTimeSeries = useCallback(
    (parameterId: string) => {
      if (!parameters?.length) return;
      const updated = applyTimeSeriesFixForParameter(
        parameterId,
        timeSeriesConfigurations,
        parameters,
        t("admin.widgets.fields.model_map.general.untitled"),
      );
      if (updated !== timeSeriesConfigurations) {
        form.change("configuration.parameter_time_series_configurations", updated);
      }
    },
    [form, parameters, timeSeriesConfigurations, t],
  );

  const addMapLayer = useCallback(
    (details: ObservationsMapParameterMapLayerConfiguration) => {
      if (selectedMapLayerIds.includes(details.parameter_map_layer_id)) return;
      form.change("configuration.parameter_map_layer_configurations", [
        ...parameterMapLayerConfigurations,
        details,
      ]);
    },
    [form, parameterMapLayerConfigurations, selectedMapLayerIds],
  );

  const removeMapLayer = useCallback(
    (mapLayerId: string) => {
      if (!selectedMapLayerIds.includes(mapLayerId)) return;
      const newConfigs = parameterMapLayerConfigurations.filter(
        (c) => c.parameter_map_layer_id !== mapLayerId,
      );
      form.change("configuration.parameter_map_layer_configurations", newConfigs);
    },
    [form, parameterMapLayerConfigurations, selectedMapLayerIds],
  );

  const reorderMapLayers = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.parameter_map_layer_configurations",
        toMoved(parameterMapLayerConfigurations, fromIndex, toIndex),
      );
    },
    [form, parameterMapLayerConfigurations],
  );

  const renameMapLayer = useCallback(
    (mapLayerId: string, name: string) => {
      let nextName = name.trim();
      if (!nextName) {
        nextName = mapLayers?.find((it) => it.id === mapLayerId)?.label ?? "(unnamed)";
      }
      const index = parameterMapLayerConfigurations.findIndex(
        (it) => it.parameter_map_layer_id === mapLayerId,
      );
      if (index >= 0) {
        form.change(
          `configuration.parameter_map_layer_configurations[${index}].label`,
          nextName,
        );
      }
    },
    [form, parameterMapLayerConfigurations, mapLayers],
  );

  const updateMapLayerEnabledByDefault = useCallback(
    (mapLayerId: string, enabled: boolean) => {
      const index = parameterMapLayerConfigurations.findIndex(
        (it) => it.parameter_map_layer_id === mapLayerId,
      );
      if (index >= 0) {
        form.change(
          `configuration.parameter_map_layer_configurations[${index}].enabled_by_default`,
          enabled,
        );
      }
    },
    [form, parameterMapLayerConfigurations],
  );

  const addStaticOverlay = useCallback(
    (details: StaticMapOverlaysOverlayConfiguration) => {
      if (selectedStaticOverlayIds.includes(details.static_map_overlay_id)) return;
      form.change("configuration.static_map_overlay_configurations", [
        ...staticOverlayConfigurations,
        details,
      ]);
    },
    [form, staticOverlayConfigurations, selectedStaticOverlayIds],
  );

  const removeStaticOverlay = useCallback(
    (staticOverlayId: string) => {
      if (!selectedStaticOverlayIds.includes(staticOverlayId)) return;
      form.change(
        "configuration.static_map_overlay_configurations",
        staticOverlayConfigurations.filter(
          (c) => c.static_map_overlay_id !== staticOverlayId,
        ),
      );
    },
    [form, staticOverlayConfigurations, selectedStaticOverlayIds],
  );

  const reorderStaticOverlays = useCallback(
    (fromIndex: number, toIndex: number) => {
      form.change(
        "configuration.static_map_overlay_configurations",
        toMoved(staticOverlayConfigurations, fromIndex, toIndex),
      );
    },
    [form, staticOverlayConfigurations],
  );

  const renameStaticOverlay = useCallback(
    (staticOverlayId: string, name: string) => {
      let nextName = name.trim();
      if (!nextName) {
        nextName =
          staticOverlays?.find((it) => it.id === staticOverlayId)?.name ?? "(unnamed)";
      }
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].label`,
          nextName,
        );
      }
    },
    [form, staticOverlayConfigurations, staticOverlays],
  );

  const updateStaticOverlayColor = useCallback(
    (staticOverlayId: string, color: string) => {
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].color`,
          color,
        );
      }
    },
    [form, staticOverlayConfigurations],
  );

  const updateStaticOverlayEnabledByDefault = useCallback(
    (staticOverlayId: string, enabled: boolean) => {
      const index = staticOverlayConfigurations.findIndex(
        (it) => it.static_map_overlay_id === staticOverlayId,
      );
      if (index >= 0) {
        form.change(
          `configuration.static_map_overlay_configurations[${index}].enabled_by_default`,
          enabled,
        );
      }
    },
    [form, staticOverlayConfigurations],
  );

  useEffect(() => {
    if (!boundsFromConfig) {
      form.change("configuration.initial_viewport_bounds", initialViewportPresets.nl);
    }
  }, [boundsFromConfig, form]);

  useEffect(() => {
    const style = configuration.base_layer_style;
    if (typeof style !== "string" || !style.trim()) {
      form.change(
        "configuration.base_layer_style",
        ObservationsMapBaseLayerStyle.Monochrome,
      );
    }
  }, [configuration.base_layer_style, form]);

  // Once all programmatic form.change() calls have settled (including the async
  // unit backfill that depends on observedParameters), reset the RFF baseline and
  // capture our own snapshot so hasChanges only reflects real user edits.
  useEffect(() => {
    if (parametersLoading) {
      setDirtyBaselineReady(false);
      return;
    }
    form.reset(form.getState().values);
    // Normalize before storing so client-only fields (e.g. display group form IDs)
    // are stripped and don't produce false positives in the dirty check.
    baselineRef.current = canonicalizeWidgetUpdatePayload(form.getState().values as Widget);
    setDirtyBaselineReady(true);
  }, [parametersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedCurrent = canonicalizeWidgetUpdatePayload(formState.values as Widget);
  const hasChanges =
    baselineRef.current !== null && !isEqual(normalizedCurrent, baselineRef.current);

  return {
    form,
    hasChanges,
    dirtyBaselineReady,
    configuration,
    title: formState.values.title as string,
    initialViewportBounds,
    setInitialViewportBounds,
    updateInitialViewportBoundsField,
    baseLayerStyle,
    setBaseLayerStyle,
    mapLayers,
    selectedMapLayers: parameterMapLayerConfigurations,
    selectedMapLayerIds,
    addMapLayer,
    removeMapLayer,
    reorderMapLayers,
    renameMapLayer,
    updateMapLayerEnabledByDefault,
    staticOverlays,
    staticOverlayConfigurations,
    selectedStaticOverlayIds,
    addStaticOverlay,
    removeStaticOverlay,
    reorderStaticOverlays,
    renameStaticOverlay,
    updateStaticOverlayColor,
    updateStaticOverlayEnabledByDefault,
    parameters,
    parametersLoading,
    timeSeriesConfigurations,
    addTimeSeriesConfig,
    removeTimeSeriesConfig,
    reorderTimeSeriesConfigs,
    updateTimeSeriesConfig,
    displayGroups,
    addDisplayGroup,
    removeDisplayGroup,
    reorderDisplayGroups,
    updateDisplayGroup,
    setDisplayGroups,
    showAllLocations,
    setShowAllLocations,
    weatherStationsEnabledByDefault,
    setWeatherStationsEnabledByDefault,
    ensureParameterInTimeSeries,
  };
}

function roundCoord(n: number): number {
  return Math.round(n * 1_000) / 1_000;
}

function parseCoord(v: unknown): number | undefined {
  if (v === "" || v === undefined || v === null) return undefined;
  const n = Number(v);
  return n === n ? n : undefined;
}

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampLatitude(value: number): number {
  return clamp(-90, 90, value);
}

function clampLongitude(value: number): number {
  return clamp(-180, 180, value);
}
